import type { BindAction } from '../core/bindings';
import { clear, h } from './dom';
import { activeDevice, glyphsFor, onDeviceChange, type Glyph } from './inputPrompts';

/** One button/key glyph: keycap, colored PlayStation shape, Xbox letter, shoulder pill or touch label. */
export function glyphEl(g: Glyph): HTMLElement {
  const cls = ['glyph', ...g.kind.split(' ').map((t) => `glyph-${t}`)].join(' ');
  return h('span', { class: cls, 'aria-label': g.text }, g.text);
}

/**
 * A prompt that re-renders whenever the active device or the bindings change,
 * and drops its subscription once it has been removed from the page.
 */
export function livePrompt(render: () => (Node | string | null)[], attrs: Record<string, unknown> = {}): HTMLElement {
  const el = h('span', { class: 'live-prompt', ...attrs });
  let wasAttached = false;
  const draw = () => {
    clear(el);
    el.dataset.device = activeDevice().kind;
    for (const part of render()) if (part !== null) el.append(part);
  };
  const stop = onDeviceChange(() => {
    if (el.isConnected) wasAttached = true;
    else if (wasAttached) { stop(); return; }
    draw();
  });
  draw();
  return el;
}

/** "[glyph] Label" for one action, e.g. "[Space] Ability" / "[✕] Ability" / "Ability button". */
export function actionPrompt(action: BindAction | 'move', label: string): HTMLElement {
  return livePrompt(() => {
    const glyphs = glyphsFor(action);
    // Touch labels already say what to do ("Tap the ability button").
    if (activeDevice().kind === 'touch') return [h('span', { class: 'prompt-label' }, glyphs[0]?.text ?? label)];
    return [...glyphs.map(glyphEl), h('span', { class: 'prompt-label' }, label)];
  }, { class: 'live-prompt action-prompt' });
}

/** "Press any key / Press any button / Tap to …" for attract mode and replays. */
export function exitHint(verb: string): HTMLElement {
  return livePrompt(() => {
    const kind = activeDevice().kind;
    return [kind === 'touch' ? `Tap to ${verb}` : kind === 'gamepad' ? `Press any button to ${verb}` : `Press any key to ${verb}`];
  }, { class: 'live-prompt sub' });
}
